import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { logError } from '@app/core/functions/tap-log';
import { UnifiedPharmacy } from './unified-pharmacy.model';
import {
  ClinicalPharmacyDoc,
  CLINICAL_PHARMACIES_PATH,
  CLINICAL_PHARMACIES_META_DOC,
  PharmacySyncMeta,
  fromClinicalPharmacyDoc,
} from './clinical-pharmacy.model';
import { ProviderStore } from '../state/provider/provider.store';
import { ProviderQuery } from '../state/provider/provider.query';

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/**
 * Reads/writes the unified pharmacy catalogue at `Configuration/ClinicalPharmacies/ClinicalPharmacies`.
 * The picker searches the whole catalogue IN MEMORY (Firestore can't do OR-across-fields / substring),
 * so the full catalogue is loaded once into the session-scoped `ProviderStore` and filtered client-side.
 * It is reloaded only when the per-vendor last-sync signature changes; the store is flushed on logout /
 * provider switch, so the next open reloads for the new provider.
 */
@Injectable({ providedIn: 'root' })
export class ClinicalPharmacyService {
  private catalogueLoadInFlight = false;

  constructor(
    private firestore: AngularFirestore,
    private providerStore: ProviderStore,
    private providerQuery: ProviderQuery
  ) {}

  /** The cached catalogue (empty until `ensureCatalogueFresh()` has loaded it). */
  catalogue$(): Observable<UnifiedPharmacy[]> {
    return this.providerQuery.pharmacies$;
  }

  /** True once the catalogue has been loaded at least once this session (drives the picker spinner). */
  catalogueLoaded$(): Observable<boolean> {
    return this.providerQuery.pharmaciesLoaded$;
  }

  /**
   * Populate/refresh the in-memory catalogue. Loads the whole subcollection when the cache is EMPTY
   * (regardless of whether sync data exists yet) or when the per-vendor sync signature has changed;
   * otherwise serves the cache. Safe to call on every picker open — it only reads the (cheap) meta
   * doc unless a reload is actually needed, and a failed load never leaves the picker stuck.
   */
  ensureCatalogueFresh(): void {
    this.syncMeta$()
      .pipe(
        first(),
        map(meta => this.buildSyncSignature(meta)),
        filter(sig => this.shouldReloadCatalogue(sig)),
        tap(() => (this.catalogueLoadInFlight = true)),
        switchMap(sig =>
          this.readAll$().pipe(
            first(),
            tap(pharmacies => this.providerStore.update({ pharmacies, pharmaciesSyncSignature: sig })),
            catchError(err => {
              logError('Failed to load pharmacy catalogue', err);
              // stamp a signature so the picker leaves the spinner (shows the empty state); the cache
              // is still empty, so the next open retries via the empty-cache branch of shouldReload.
              this.providerStore.update({ pharmaciesSyncSignature: this.providerQuery.getValue().pharmaciesSyncSignature ?? '' });
              return of(null);
            }),
            finalize(() => (this.catalogueLoadInFlight = false))
          )
        )
      )
      .subscribe();
  }

  /** Reload when a load isn't already running AND (the cache is empty — sync data may not exist yet — or it's stale). */
  private shouldReloadCatalogue(sig: string): boolean {
    if (this.catalogueLoadInFlight) {
      return false;
    }
    const state = this.providerQuery.getValue();
    return state.pharmacies.length === 0 || sig !== state.pharmaciesSyncSignature;
  }

  /** One read of the entire catalogue (no filter/orderBy → no composite index). */
  private readAll$(): Observable<UnifiedPharmacy[]> {
    return this.firestore
      .collection<ClinicalPharmacyDoc>(CLINICAL_PHARMACIES_PATH)
      .get()
      .pipe(
        first(),
        map(snap => snap.docs.map(d => fromClinicalPharmacyDoc(d.data() as ClinicalPharmacyDoc)))
      );
  }

  /** Stable signature of the catalogue's per-vendor sync stamps; changes iff any vendor was re-synced. */
  private buildSyncSignature(meta: Record<string, PharmacySyncMeta>): string {
    return Object.keys(meta)
      .sort()
      .map(vendorId => {
        const m = meta[vendorId];
        const ts: any = m.lastSyncedAt;
        const stamp = ts && typeof ts.seconds === 'number' ? ts.seconds : String(ts ?? '');
        return `${vendorId}:${stamp}:${m.count ?? 0}`;
      })
      .join('|');
  }

  /** Idempotent batched upsert by docId (used by admin ingest + migration). */
  async upsertMany(docs: ClinicalPharmacyDoc[]): Promise<void> {
    const col = this.firestore.collection<ClinicalPharmacyDoc>(CLINICAL_PHARMACIES_PATH);
    for (const group of chunk(docs, 450)) {
      const batch = this.firestore.firestore.batch();
      group.forEach(d => batch.set(col.doc(d.docId).ref, dropNullUndefined(d) as ClinicalPharmacyDoc));
      await batch.commit();
    }
  }

  /**
   * TRUE replace of one vendor's slice of the catalogue: upsert the new docs, then delete that
   * vendor's docs no longer present (pharmacies removed upstream, or docIds that changed shape).
   * Other vendors' docs are untouched.
   */
  async replaceVendorCatalogue(vendorId: string, docs: ClinicalPharmacyDoc[]): Promise<void> {
    const keep = new Set(docs.map(d => d.docId));
    const snap = await this.firestore
      .collection<ClinicalPharmacyDoc>(CLINICAL_PHARMACIES_PATH, ref => ref.where('vendorId', '==', vendorId))
      .get()
      .pipe(first())
      .toPromise();
    const stale = snap.docs.filter(d => !keep.has(d.id));
    await this.upsertMany(docs);
    for (const group of chunk(stale, 450)) {
      const batch = this.firestore.firestore.batch();
      group.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  }

  /**
   * Stamp a vendor's sync metadata (when/how-many/by-whom) onto the catalogue's parent doc.
   * Merged under a `syncMeta` map keyed by vendorId, so other vendors' entries are preserved.
   */
  async recordSync(
    vendorId: string,
    meta: { vendorLabel: string; count: number; byUserId?: string; byUsername?: string; byFullName?: string }
  ): Promise<void> {
    const entry: PharmacySyncMeta = {
      vendorLabel: meta.vendorLabel,
      count: meta.count,
      lastSyncedAt: firebase.firestore.FieldValue.serverTimestamp(),
      byUserId: meta.byUserId ?? null,
      byUsername: meta.byUsername ?? null,
      byFullName: meta.byFullName ?? null,
    } as PharmacySyncMeta;
    await this.firestore.doc(CLINICAL_PHARMACIES_META_DOC).set({ syncMeta: { [vendorId]: entry } }, { merge: true });
  }

  /** Live per-vendor sync metadata map (keyed by vendorId) for the admin screen. */
  syncMeta$(): Observable<Record<string, PharmacySyncMeta>> {
    return this.firestore
      .doc(CLINICAL_PHARMACIES_META_DOC)
      .valueChanges()
      .pipe(map((data: any) => (data?.syncMeta ?? {}) as Record<string, PharmacySyncMeta>));
  }

  /**
   * Global per-vendor kill-switch, stored as a `disabledVendors` map on the catalogue's parent doc
   * (`{ easyrx: true }` = EasyRx off for EVERY provider). Live — flipping it takes effect in open
   * sessions without a deploy. Complements the per-provider Allow…Scripts options.
   */
  // One shared Firestore listener on the meta doc — `registry.allowed$()` is called per prescription
  // row, so without shareReplay N rows would open N identical realtime listeners.
  private readonly disabledVendorsShared$: Observable<Record<string, boolean>> = this.firestore
    .doc(CLINICAL_PHARMACIES_META_DOC)
    .valueChanges()
    .pipe(
      map((data: any) => (data?.disabledVendors ?? {}) as Record<string, boolean>),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  disabledVendors$(): Observable<Record<string, boolean>> {
    return this.disabledVendorsShared$;
  }

  setVendorDisabled(vendorId: string, disabled: boolean): Promise<void> {
    return this.firestore.doc(CLINICAL_PHARMACIES_META_DOC).set({ disabledVendors: { [vendorId]: disabled } }, { merge: true });
  }
}
